import React from "react";
import { Control, FieldErrors, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Label, Select, TextInput } from "flowbite-react";
import { HiArrowLeft, HiCheckCircle } from "react-icons/hi";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import CountryOptions from "../../../public/CountryOptions";
import moment from "moment";
import {
  CustomRegistrationFormData,
  ProcessDataConfig,
  RegistrationType,
} from "../types";
import { CustomPhoneNumber } from "../../../atoms/CustomPhoneNumber";

type FieldName =
  | "user_first_name"
  | "user_last_name"
  | "user_user_name"
  | "user_phone"
  | "user_country_code"
  | "user_birth_date"
  | "user_email"
  | "user_password";

const previewLabelClass = "block text-sm font-medium text-gray-700 mb-1";

const PreviewInput: React.FC<{
  label: string;
  placeholder: string;
  type?: string;
  max?: string;
}> = ({ label, placeholder, type = "text", max }) => (
  <div>
    <Label className={previewLabelClass}>{label}</Label>
    <TextInput
      disabled
      placeholder={placeholder}
      type={type}
      max={max}
      className="w-full"
    />
  </div>
);

const PreviewPhoneInput: React.FC<{
  label: string;
  placeholder: string;
}> = ({ label, placeholder }) => (
  <div>
    <Label className={previewLabelClass}>{label}</Label>
    <div className="relative flex items-center opacity-50">
      <button
        disabled
        className="shrink-0 z-10 inline-flex items-center py-2.5 md:py-2 px-2 text-sm font-medium text-center text-gray-900 bg-gray-50 border border-gray-300 rounded-s-md"
        type="button"
      >
        +1
        <svg
          className="w-2.5 h-2.5 ms-2.5"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m1 1 4 4 4-4"
          />
        </svg>
      </button>
      <input
        disabled
        type="text"
        placeholder={placeholder}
        className="block p-2.5 md:p-2 w-full z-20 text-base md:text-sm text-gray-900 bg-white rounded-e-md border-s-0 border border-gray-300"
      />
    </div>
  </div>
);

const isFieldRequired = (
  config: ProcessDataConfig,
  fieldName: FieldName
): boolean => {
  const fields = config.fields as
    | Partial<Record<FieldName, { required?: boolean }>>
    | undefined;
  return !!fields?.[fieldName]?.required;
};

const isFieldVisible = (
  config: ProcessDataConfig,
  fieldName: FieldName
): boolean => {
  const fields = config.fields as
    | Partial<Record<FieldName, { visible?: boolean }>>
    | undefined;
  return !!fields?.[fieldName]?.visible;
};

interface Step2RegistrationFormProps {
  config: ProcessDataConfig;
  control: Control<CustomRegistrationFormData> | null;
  errors: FieldErrors<CustomRegistrationFormData> | null;
  type: RegistrationType;
  isSubmitting: boolean;
  onBack?: () => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => void | Promise<void>;
  isLastStep?: boolean;
  isPreview?: boolean;
  hasExistingAuth?: boolean;
  ownerCountryCode?: string;
}

export const Step2RegistrationForm: React.FC<Step2RegistrationFormProps> = ({
  config,
  control,
  errors,
  type,
  isSubmitting,
  onBack,
  onSubmit,
  isLastStep,
  isPreview = false,
  hasExistingAuth = false,
  ownerCountryCode,
}) => {
  const { t } = useTranslation("clientView");
  const shouldUsePreview = isPreview || control === null;

  const renderInput = (
    field: FieldName,
    labelKey: string,
    placeholderKey: string,
    type:
      | "number"
      | "text"
      | "email"
      | "password"
      | "date"
      | "hidden"
      | "datetime-local"
      | "url"
      | undefined = "text",
    extraProps: Record<string, any> = {},
    checkVisibility: boolean = true
  ) => {
    if (checkVisibility && !isFieldVisible(config, field)) return null;

    const label = `${t(labelKey)}${isFieldRequired(config, field) ? " *" : ""}`;
    const placeholder = t(placeholderKey) as string;

    return shouldUsePreview ? (
      field === "user_phone" ? (
        <PreviewPhoneInput label={label} placeholder={placeholder} />
      ) : (
        <PreviewInput
          label={label}
          placeholder={placeholder}
          type={type}
          {...extraProps}
        />
      )
    ) : field === "user_phone" ? (
      <CustomPhoneNumber
        name={field}
        control={control!}
        label={label}
        placeholder={placeholder}
        type={type}
        error={errors?.[field]}
        helperText={errors?.[field]}
        defaultCountryCode={ownerCountryCode}
        {...extraProps}
      />
    ) : (
      <CustomInput
        name={field}
        control={control!}
        label={label}
        placeholder={placeholder}
        type={type}
        error={errors?.[field]}
        helperText={errors?.[field]}
        {...extraProps}
      />
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {!shouldUsePreview && onBack && (
        <CustomButton
          variant="none"
          size="xs"
          onClick={onBack}
          icon={<HiArrowLeft size={16} className="rtl:rotate-180" />}
          title={t("customRegistration.page.step2.backButton")}
          className="mb-4 justify-start px-0 py-1 text-sm font-medium text-blue-600 hover:text-blue-700"
        />
      )}

      {renderInput(
        "user_email",
        "register.form.email",
        "register.form.emailPlaceholder",
        "email",
        {
          disabled: hasExistingAuth,
          style: { paddingInlineEnd: "2.25rem" },
          rightComponent: (
            <HiCheckCircle
              className="absolute top-1/2 -translate-y-1/2 w-5 h-5 text-green-500 pointer-events-none"
              style={{ insetInlineEnd: "0.75rem" }}
              aria-label="Email is valid"
            />
          ),
        },
        false
      )}

      {renderInput(
        "user_first_name",
        "register.form.firstName",
        "register.form.firstNamePlaceholder"
      )}
      {renderInput(
        "user_last_name",
        "register.form.lastName",
        "register.form.lastNamePlaceholder"
      )}
      {renderInput(
        "user_user_name",
        "register.form.userName",
        "register.form.userNamePlaceholder"
      )}
      {renderInput(
        "user_birth_date",
        "register.form.birthDate",
        "register.form.birthDatePlaceholder",
        "date",
        {
          min: moment().subtract(120, "years").format("YYYY-MM-DD"),
          max: moment().format("YYYY-MM-DD"),
        }
      )}

      {isFieldVisible(config, "user_country_code") && (
        <div>
          <Label htmlFor="country" className={previewLabelClass}>
            {t("customRegistration.page.step2.fields.country")}
            {isFieldRequired(config, "user_country_code") ? " *" : ""}
          </Label>

          {shouldUsePreview ? (
            <Select id="user_country_code" disabled className="w-full">
              <option value="">
                {t("customRegistration.page.step2.fields.countryPlaceholder")}
              </option>
            </Select>
          ) : (
            <Controller
              name="user_country_code"
              control={control!}
              render={({ field }) => (
                <Select
                  id="user_country_code"
                  {...field}
                  onChange={(e) => field.onChange(e.target.value)}
                  className="w-full"
                >
                  <option value="">
                    {t(
                      "customRegistration.page.step2.fields.countryPlaceholder"
                    )}
                  </option>
                  <CountryOptions />
                </Select>
              )}
            />
          )}
          {errors?.user_country_code && (
            <p className="text-red-500 text-xs mt-1">
              {errors.user_country_code.message}
            </p>
          )}
        </div>
      )}

      {renderInput(
        "user_phone",
        "customRegistration.page.step2.fields.phone",
        "customRegistration.page.step2.fields.phonePlaceholder",
        "number",
        {
          maxLength: 13,
        }
      )}

      {!shouldUsePreview && (
        <div className="pt-4">
          <CustomButton
            variant="primary"
            type="submit"
            title={
              isSubmitting
                ? isLastStep
                  ? t("customRegistration.page.step3.submitting")
                  : t("customRegistration.page.step2.processing")
                : isLastStep
                ? t("customRegistration.page.step3.submitButton")
                : t("customRegistration.page.step2.nextButton")
            }
            isLoading={isSubmitting}
            disabled={isSubmitting}
            className="w-full"
          />
        </div>
      )}
    </form>
  );
};
